import React from 'react';
import { FaChevronLeft, FaChevronRight, FaExternalLinkAlt } from 'react-icons/fa';
import {
  getDisplayUrl,
  getRowIndicatorType,
  getTenantDisplayName,
} from '../tenantHelpers';
import TenantActionMenu from './TenantActionMenu';
import TenantStatus from './TenantStatus';

const TenantTable = ({
  tenants,
  getActions,
  onOpen,
  onAction,
  emptyState,
  isLoading,
  activeTab,
  currentPage,
  rowsPerPage,
  onPageChange,
}) => {
  if (isLoading) {
    return (
      <div className="stm-table-shell">
        <div className="stm-table-wrap">
          <table className="stm-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Labels</th>
                <th>Current Status</th>
                <th className="stm-table__actions-col"> </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }).map((_, index) => (
                <tr key={`skeleton-${index}`} className="stm-table-row stm-table-row--skeleton">
                  <td><span className="stm-skeleton stm-skeleton--md" /><span className="stm-skeleton stm-skeleton--sm" /></td>
                  <td><span className="stm-skeleton stm-skeleton--chip" /></td>
                  <td><span className="stm-skeleton stm-skeleton--chip" /><span className="stm-skeleton stm-skeleton--sm" /></td>
                  <td><span className="stm-skeleton stm-skeleton--icon" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!tenants.length) {
    return <div className="stm-table-shell">{emptyState}</div>;
  }

  const totalPages = Math.max(1, Math.ceil(tenants.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const visibleTenants = tenants.slice(startIndex, startIndex + rowsPerPage);
  const endIndex = Math.min(startIndex + rowsPerPage, tenants.length);

  return (
    <div className="stm-table-shell">
      <div className="stm-table-wrap">
        <table className="stm-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Labels</th>
              <th>Current Status</th>
              <th className="stm-table__actions-col"> </th>
            </tr>
          </thead>
          <tbody>
            {visibleTenants.map((tenant) => {
              const labels = tenant.labels || [];
              const indicator = getRowIndicatorType(tenant);
              const displayUrl = getDisplayUrl(tenant.tenantUrl);

              return (
                <tr
                  key={tenant.id}
                  className={`stm-table-row ${indicator ? `stm-table-row--${indicator}` : ''}`}
                  onClick={() => onOpen(tenant)}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') onOpen(tenant);
                  }}
                >
                  <td data-label="Tenant" className="stm-table__tenant-col">
                    <div className="stm-tenant-cell">
                      <div className="stm-tenant-cell__title">
                        {getTenantDisplayName(tenant)}
                        {activeTab === 'all' && (
                          <span className="stm-tenant-cell__type">{tenant.tenantType}</span>
                        )}
                      </div>
                      <a
                        className="stm-tenant-cell__url"
                        href={tenant.tenantUrl}
                        title={tenant.tenantUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <span>{displayUrl}</span>
                        <FaExternalLinkAlt aria-hidden />
                      </a>
                    </div>
                  </td>
                  <td data-label="Labels" onClick={(event) => event.stopPropagation()}>
                    {labels.length ? (
                      <div className="stm-labels-cell" title={labels.join(', ')}>
                        {labels.slice(0, 2).map((label) => (
                          <span key={label} className="stm-label-chip">{label}</span>
                        ))}
                        {labels.length > 2 && (
                          <span className="stm-label-more" title={labels.slice(2).join(', ')}>
                            +{labels.length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="stm-muted">No labels</span>
                    )}
                  </td>
                  <td data-label="Current Status">
                    <TenantStatus tenant={tenant} />
                  </td>
                  <td data-label="Actions" className="stm-table__actions-col" onClick={(event) => event.stopPropagation()}>
                    <TenantActionMenu tenant={tenant} actions={getActions(tenant)} onAction={onAction} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="stm-pagination">
        <span>Showing {startIndex + 1}–{endIndex} of {tenants.length}</span>
        <div className="stm-pagination__controls">
          <button type="button" className="stm-pagination__btn" onClick={() => onPageChange(safePage - 1)} disabled={safePage <= 1} aria-label="Previous page">
            <FaChevronLeft />
          </button>
          {Array.from({ length: totalPages }).slice(0, 5).map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                type="button"
                className={`stm-pagination__page ${safePage === page ? 'is-active' : ''}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            );
          })}
          <button type="button" className="stm-pagination__btn" onClick={() => onPageChange(safePage + 1)} disabled={safePage >= totalPages} aria-label="Next page">
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantTable;
